const MINUTES_PER_HOUR = 60

/**
 * Threshold separating "decimal fraction" from "minutes" when the input
 * uses a comma or a dot (e.g. "1,30" vs "1,5").
 *
 * Below this value, the part after the separator is treated as a decimal
 * fraction (value-based rule, per V2 design):
 *   - "1,5"  → 1.5   (fraction, 5 < 10)
 *   - "1,05" → 1.05  (fraction, 5 < 10)
 * At or above this value, it is treated as minutes:
 *   - "1,30" → 1.5     (1h30)
 *   - "2,25" → 2.4167  (2h25)
 *   - "1,75" → 2.25    (1h75, overflow accepted)
 */
const DECIMAL_FRACTION_THRESHOLD = 10

const HOUR_MARKER_PATTERN = /^(\d+)h(\d*)$/
const COLON_PATTERN = /^(\d+):(\d+)$/
const SEPARATOR_PATTERN = /^(\d+)[,.](\d+)$/
const INTEGER_PATTERN = /^\d+$/

/**
 * Parses a flexible, driver-entered time string into a decimal number of
 * hours. Accepts the many formats found in real V1 time sheets:
 *
 *   "1h30", "1H30", "1h", "1:30", "1,30", "1.30", "1,5", "1.5", "2", "0"
 *
 * Disambiguation of "X,YY" and "X.YY" (V2 fix):
 * - The V1 naively did `str_replace(',', '.', raw) → floatval`, so
 *   "1,30" became 1.3 hours — a silent data-loss bug (a driver writing
 *   "1,30" means 1h30 = 1.5h).
 * - V2 uses a value-based rule: if the part after the separator is
 *   `>= 10`, it is minutes (0..59+ accepted, overflow rolls into hours);
 *   otherwise it is a decimal fraction.
 *
 * Returns 0 for any empty, malformed or negative input. Rationale: this
 * is field-entered data; a silent 0 is the V1-compatible default and the
 * calling form validation layer already surfaces the issue to the user.
 *
 * The function is pure: no I/O, no `Date`, no shared state.
 */
export function parseFlexibleHours(input: string): number {
  const normalized = normalize(input)
  if (normalized === '') {
    return 0
  }

  const hourMarker = HOUR_MARKER_PATTERN.exec(normalized)
  if (hourMarker !== null) {
    const [, hoursPart = '', minutesPart = ''] = hourMarker
    return hoursPlusMinutes(hoursPart, minutesPart)
  }

  const colon = COLON_PATTERN.exec(normalized)
  if (colon !== null) {
    const [, hoursPart = '', minutesPart = ''] = colon
    return hoursPlusMinutes(hoursPart, minutesPart)
  }

  const separator = SEPARATOR_PATTERN.exec(normalized)
  if (separator !== null) {
    const [, hoursPart = '', fractionPart = ''] = separator
    return decimalOrMinutes(hoursPart, fractionPart)
  }

  if (INTEGER_PATTERN.test(normalized)) {
    return Number(normalized)
  }

  return 0
}

function normalize(input: string): string {
  return input.trim().toLowerCase().replace(/\s+/g, '')
}

function hoursPlusMinutes(hoursPart: string, minutesPart: string): number {
  const hours = Number(hoursPart)
  const minutes = minutesPart === '' ? 0 : Number(minutesPart)
  return hours + minutes / MINUTES_PER_HOUR
}

function decimalOrMinutes(hoursPart: string, fractionPart: string): number {
  const fractionValue = Number(fractionPart)
  if (fractionValue >= DECIMAL_FRACTION_THRESHOLD) {
    return Number(hoursPart) + fractionValue / MINUTES_PER_HOUR
  }
  return Number(`${hoursPart}.${fractionPart}`)
}
